/**
 * buildCopiloteKB(annexe) — Multi-country KB factory
 *
 * Takes the raw COPILOTE_KB skeleton (country-agnostic structure) and enriches it
 * with country-specific values from the annexe nationale (XX.json via /annexe/{pays}).
 *
 * What gets injected per module:
 *   • exemple_projet  — real project example from annexe.exemples_projets
 *   • norm name       — in valeurs_typiques "Norme" rows
 *   • FS values       — from annexe.copilote_valeurs.fs_cibles
 *   • approche label  — EC7 DA2* (France) vs EC8 (seismic) etc.
 *
 * What is NOT yet injected (future — needs template refactor of inline text):
 *   • EC8 references in conseil/alerte strings
 *   • Zone labels (IIa/IIb vs 1/2/3) in inline descriptions
 *   • City names in examples embedded in text fields
 */

import COPILOTE_KB from '../copiloteKB';

function deepClone(obj) {
  return JSON.parse(JSON.stringify(obj));
}

/**
 * Find the FS label used by this country's seismic norm.
 * EC8 (France) uses "SF_min sismique EC8" label by default.
 */
function buildFsNormNote(annexe, situation) {
  const cv = annexe?.copilote_valeurs;
  if (!cv) return null;
  const fs = cv.fs_cibles?.[situation];
  const label = cv.approche_ec7_label || '';
  const norme = label.includes('EC7') ? 'EC7 DA2* (France)' : label;
  return fs !== undefined ? `${fs} — ${norme}` : null;
}

/**
 * Inject FS values into a valeurs_typiques array.
 * Matches rows by their `param` field (case-insensitive contains).
 */
function injectFsValues(vt, annexe) {
  if (!Array.isArray(vt) || !annexe?.copilote_valeurs?.fs_cibles) return vt;
  const fs = annexe.copilote_valeurs.fs_cibles;
  return vt.map(row => {
    const p = (row.param || '').toLowerCase();
    if (p.includes('sf_min permanent') || p.includes('sf_min statique')) {
      return { ...row, val: String(fs.statique ?? row.val) };
    }
    if (p.includes('sf_min sismique') || p.includes('sismique')) {
      const note = buildFsNormNote(annexe, 'sismique');
      return { ...row, val: String(fs.sismique ?? row.val), note: note || row.note };
    }
    if (p.includes('sf_min transit') || p.includes('transitoire') || p.includes('construction')) {
      return { ...row, val: String(fs.transitoire ?? row.val) };
    }
    if (p.includes('norme')) {
      const label = annexe.copilote_valeurs?.approche_ec7_label;
      if (label) return { ...row, note: `✅ ${label}` };
    }
    return row;
  });
}

/**
 * Main factory function.
 * @param {object} annexe — full annexe nationale JSON (from /annexe/{pays})
 * @returns enriched COPILOTE_KB object
 */
export function buildCopiloteKB(annexe) {
  const kb = deepClone(COPILOTE_KB);
  if (!annexe) return kb;

  const exemples = annexe.exemples_projets || {};
  const normes = annexe.normes_par_module || {};

  for (const [moduleKey, mod] of Object.entries(kb)) {
    if (!mod?.onglets) continue;

    // Inject exemple_projet into onglet 0 utilite section
    if (exemples[moduleKey]) {
      const onglet0 = mod.onglets.find(o => o.num === 0);
      if (onglet0?.utilite) {
        onglet0.utilite.exemple_projet = exemples[moduleKey];
      }
    }

    // Inject norm info into each onglet's valeurs_typiques
    for (const onglet of mod.onglets) {
      if (Array.isArray(onglet.valeurs_typiques)) {
        onglet.valeurs_typiques = injectFsValues(onglet.valeurs_typiques, annexe);
      }
    }

    // Attach norm metadata at module level for display in copilote header
    if (normes[moduleKey]) {
      mod._norme = normes[moduleKey];
    }
  }

  return kb;
}
